import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, FileText, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { AutosaveIndicator, ProgressBar } from "@/components/common";
import { API, evalSessionKey } from "@/lib/api";
import { PlenaLogo } from "@/components/brand/PlenaLogo";
import { useEvaluation } from "./EvaluatorApp";
import { JobButton } from "./EvaluatorHeader";
import { CriterionCard } from "./CriterionCard";

function OverallScore({ value, onChange }) {
  const [local, setLocal] = useState(value ?? "");
  const timer = useRef(null);
  useEffect(() => { setLocal(value ?? ""); }, [value]);
  const commit = (v) => { clearTimeout(timer.current); timer.current = setTimeout(() => onChange(v === "" ? null : Math.max(0, Math.min(100, Number(v)))), 400); };
  const onInput = (v) => { setLocal(v); commit(v); };
  return (
    <div className="flex items-center gap-5">
      <input type="range" min="0" max="100" step="1" value={local === "" ? 0 : local} onChange={(e) => onInput(e.target.value)} aria-label="Genel uygunluk puanı" data-testid="overall-slider" className="h-1.5 flex-1 cursor-pointer accent-[#0F172A]" />
      <div className="flex items-center gap-2">
        <Input type="number" min="0" max="100" value={local} onChange={(e) => onInput(e.target.value)} data-testid="overall-input" className="h-11 w-20 text-center font-heading text-lg font-bold" placeholder="—" />
        <span className="text-sm text-plena-text2">/ 100</span>
      </div>
    </div>
  );
}

function PdfViewer({ src, name }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center text-sm text-plena-text2" data-testid="cv-failed"><FileText className="h-5 w-5" /><p>CV yüklenemedi.</p><a href={src} target="_blank" rel="noreferrer" className="underline">Yeni sekmede açmayı deneyin</a></div>;
  return <iframe title={`${name} CV`} src={src} className="pdf-frame" data-testid="cv-viewer" onError={() => setFailed(true)} />;
}

export default function CandidatePage() {
  const { cid } = useParams();
  const { ctx, token, saveCriterion, saveComment, saveOverall, saveState } = useEvaluation();
  const nav = useNavigate();
  const [cvOpen, setCvOpen] = useState(false);
  const idx = ctx.candidates.findIndex((c) => c.id === cid);
  const cand = ctx.candidates[idx];
  const evals = ctx.evaluations[cid] || { criteria: {}, general_comment: null };
  const [comment, setComment] = useState(evals.general_comment || "");
  const timer = useRef(null);
  useEffect(() => { setComment(ctx.evaluations[cid]?.general_comment || ""); window.scrollTo(0, 0); }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!cand) return <div className="p-10 text-sm text-plena-text2">Aday bulunamadı. <Link to={`/e/${token}/candidates`} className="underline">Adaylara dön</Link></div>;

  const next = ctx.candidates[idx + 1];
  const prev = ctx.candidates[idx - 1];
  const session = sessionStorage.getItem(evalSessionKey(token));
  const pdfSrc = `${API}/e/${token}/candidates/${cid}/document${session ? `?s=${encodeURIComponent(session)}` : ""}`;
  const onComment = (v) => { setComment(v); clearTimeout(timer.current); timer.current = setTimeout(() => saveComment(cid, v || null).catch(() => {}), 700); };
  const done = cand.progress.status === "COMPLETED";
  const pos = Object.fromEntries(ctx.category_ranking.map((r) => [r.criterion_id, r.position]));
  const orderedCriteria = [...ctx.criteria].sort((a, b) => (pos[a.id] ?? 99) - (pos[b.id] ?? 99));

  return (
    <div className="flex h-screen flex-col">
      <header className="z-30 border-b border-plena-border bg-white">
        <div className="flex h-14 items-center gap-3 px-4 sm:px-6">
          <Button variant="ghost" size="sm" data-testid="back-to-candidates" onClick={() => nav(`/e/${token}/candidates`)} className="rounded-full"><ArrowLeft className="mr-1.5 h-4 w-4" /> Adaylar</Button>
          <div className="hidden h-5 w-px bg-plena-border sm:block" />
          <div className="hidden sm:block"><PlenaLogo height={18} /></div>
          <div className="ml-auto flex items-center gap-3">
            <AutosaveIndicator state={saveState} />
            <JobButton />
            <Button variant="outline" size="sm" data-testid="open-cv-mobile" onClick={() => setCvOpen(true)} className="rounded-full lg:hidden"><FileText className="mr-1.5 h-3.5 w-3.5" /> CV</Button>
          </div>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        <aside className="hidden min-h-0 w-1/2 border-r border-plena-border bg-plena-surface2 lg:block xl:w-[52%]" data-testid="cv-panel">
          <PdfViewer src={pdfSrc} name={cand.display_name} />
        </aside>

        <section className="min-h-0 flex-1 overflow-y-auto" data-testid="evaluation-panel">
          <div className="sticky top-0 z-20 border-b border-plena-border bg-white/95 px-6 py-4 backdrop-blur-md sm:px-10">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="eyebrow mb-1">Aday {idx + 1} / {ctx.candidates.length}</p>
                <h1 className="font-heading text-xl font-bold tracking-tight sm:text-2xl" data-testid="candidate-name">{cand.display_name}</h1>
                {cand.title && <p className="text-sm text-plena-text2">{cand.title}</p>}
              </div>
              <div className="w-40 shrink-0 text-right">
                <p className="mb-1.5 text-xs font-medium text-plena-text2" data-testid="criteria-progress">{cand.progress.done} / {cand.progress.total} tamamlandı</p>
                <ProgressBar value={cand.progress.done} total={cand.progress.total} tone={done ? "bg-plena-success" : "bg-plena-text"} />
              </div>
            </div>
          </div>

          <div className="fade-up px-6 py-8 sm:px-10">
            <p className="mb-6 max-w-xl text-sm text-plena-text2">Her kategori için <span className="font-medium text-plena-text">adayın CV'sindeki kanıtın gücünü</span> 1–5 arasında puanlayın — kategorinin pozisyon için önemini değil. CV'de yeterli bilgi yoksa "Yeterli bilgi yok" seçeneğini kullanın.</p>
            <div className="divide-y divide-plena-border" data-testid="criteria-list">
              {orderedCriteria.map((cr) => <CriterionCard key={cr.id} criterion={cr} value={evals.criteria[cr.id]} onChange={(payload) => saveCriterion(cid, cr.id, payload)} />)}
            </div>
            <section className="mb-10 mt-12 max-w-xl" data-testid="overall-section">
              <h2 className="font-heading text-base font-semibold md:text-lg">Genel uygunluk puanı</h2>
              <p className="mb-4 text-sm text-plena-text2">Kategori puanlarından bağımsız olarak, bu aday bu pozisyon için genel olarak ne kadar uygun? (0–100)</p>
              <OverallScore value={evals.overall_score} onChange={(v) => saveOverall(cid, v).catch(() => {})} />
            </section>
            <section className="mb-10 max-w-xl">
              <h2 className="font-heading text-base font-semibold md:text-lg">Genel değerlendirme</h2>
              <p className="mb-3 text-sm text-plena-text2">İsteğe bağlı. Bu aday hakkında eklemek istediğiniz genel bir not varsa yazabilirsiniz.</p>
              <Textarea data-testid="general-comment" value={comment} onChange={(e) => onComment(e.target.value)} rows={3} className="rounded-xl" />
            </section>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-plena-border pt-6">
              <Button variant="ghost" disabled={!prev} data-testid="prev-candidate" onClick={() => nav(`/e/${token}/candidates/${prev.id}`)} className="rounded-full"><ArrowLeft className="mr-1.5 h-4 w-4" /> Önceki aday</Button>
              <div className="flex items-center gap-3">
                {done && <span className="inline-flex items-center gap-1 text-sm font-medium text-plena-success" data-testid="candidate-complete"><Check className="h-4 w-4" /> Değerlendirildi</span>}
                {next ? <Button data-testid="next-candidate" onClick={() => nav(`/e/${token}/candidates/${next.id}`)} className="btn-press rounded-full">Sonraki aday <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
                  : <Button data-testid="finish-candidates" onClick={() => nav(`/e/${token}/candidates`)} className="btn-press rounded-full">Adaylara dön <ArrowRight className="ml-1.5 h-4 w-4" /></Button>}
              </div>
            </div>
          </div>
        </section>
      </div>

      <Sheet open={cvOpen} onOpenChange={setCvOpen}>
        <SheetContent side="bottom" className="h-[92vh] p-0" data-testid="cv-sheet">
          <SheetHeader className="border-b border-plena-border px-5 py-3 text-left"><SheetTitle className="font-heading text-base">{cand.display_name} · CV</SheetTitle></SheetHeader>
          <div className="h-[calc(92vh-56px)]"><PdfViewer src={pdfSrc} name={cand.display_name} /></div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
